import { z } from 'zod';
import type { AppSettings } from './appSettings.schema.js';

/**
 * Legal identification of the seller. Portuguese e-commerce law requires it to
 * be visible on the storefront, and EasyPay's compliance review checks for it.
 *
 * It is sourced from the server's `STORE_*` env vars — the same values already
 * used on invoice PDFs — so the site and the invoices can never disagree.
 */
export const storeInfoSchema = z.object({
  name: z.string(),
  /**
   * The person or company legally responsible, when it differs from the
   * trading name. Rosita Store is run by a sole trader, so the law requires
   * her civil name to be identified alongside the brand. Empty when the
   * trading name is itself the legal entity.
   */
  legalName: z.string(),
  nif: z.string(),
  address: z.string(),
  email: z.string(),
  /**
   * Only the number the store chose to publish (`STORE_PUBLIC_PHONE`). Empty
   * when no phone is published — which does not mean the store has none, only
   * that it is not for the storefront. Invoices use `STORE_PHONE` instead.
   */
  phone: z.string(),
});

export type StoreInfo = z.infer<typeof storeInfoSchema>;

export const STORE_INFO_DEFAULTS: StoreInfo = {
  name: 'Rosita Store',
  legalName: '',
  nif: '',
  address: '',
  email: '',
  phone: '',
};

/**
 * Values copied straight out of `.env.example` and never filled in. Printing
 * `PT000000000` as a real NIF is worse than printing nothing, so the server
 * blanks these out and the UI omits the line entirely.
 */
export const STORE_INFO_PLACEHOLDERS: readonly string[] = [
  'PT000000000',
  '+351 000 000 000',
  'Rua Exemplo, 1, 1000-001 Lisboa',
  'loja@rositastore.pt',
];

/** Blanks out unset or still-placeholder values so callers can test truthiness. */
export function sanitizeStoreInfoValue(value: string | undefined | null): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  return STORE_INFO_PLACEHOLDERS.includes(trimmed) ? '' : trimmed;
}

/** Response shape of the public `GET /api/v1/settings`. */
export interface PublicSettings extends Partial<AppSettings> {
  storeInfo: StoreInfo;
}
